import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  SxProps,
  Theme,
  ButtonBase,
} from "@mui/material";
import designTokens from "../styles/designTokens";
import Icon from "./Icon";
import { PersistentToastContext } from "../contexts/PersistentToastProvider";
import { openReportIssue } from "../store/reportIssueStore";
import { REPORT_ISSUE_TOAST_LABEL } from "../utils/appToast";

export type ToastType = "error" | "notice" | "complete";
export type ToastVariant =
  | "error"
  | "no-icon"
  | "complete"
  | "undo"
  | "close"
  | "chrome";

type ToastHorizontalOrigin = "left" | "center" | "right";
type ToastVerticalOrigin = "top" | "bottom";

export interface ToastAnchorOrigin {
  vertical: ToastVerticalOrigin;
  horizontal: ToastHorizontalOrigin;
}

type ToastStackEntry = {
  id: string;
  close: () => void;
};

let toastStackEntries: ToastStackEntry[] = [];
const toastStackListeners = new Set<() => void>();

/**
 * MUI Snackbar 기본 enter transition(`duration.enteringScreen`)과 같은 값이다.
 * 자동 종료 타이머는 Toast가 완전히 나타난 이 시점부터 시작한다.
 */
export const TOAST_ENTER_ANIMATION_MS = 225;

const notifyToastStackListeners = () => {
  toastStackListeners.forEach((listener) => listener());
};

const unregisterToast = (id: string) => {
  const nextEntries = toastStackEntries.filter((entry) => entry.id !== id);
  if (nextEntries.length === toastStackEntries.length) return;
  toastStackEntries = nextEntries;
  notifyToastStackListeners();
};

const registerToast = (entry: ToastStackEntry) => {
  toastStackEntries = [
    ...toastStackEntries.filter((candidate) => candidate.id !== entry.id),
    entry,
  ];
  const maxVisible = designTokens.semantic.toast.default.stack.maxVisible;
  const overflow = toastStackEntries.slice(
    0,
    Math.max(0, toastStackEntries.length - maxVisible),
  );
  toastStackEntries = toastStackEntries.slice(-maxVisible);
  notifyToastStackListeners();
  overflow.forEach((candidate) => candidate.close());
};

const getToastStackIndex = (id: string): number | null => {
  const index = toastStackEntries.findIndex((entry) => entry.id === id);
  return index < 0 ? null : index;
};

type ToastStatus = "error" | "complete";

type ToastVariantConfig = {
  status: ToastStatus | null;
  showCloseButton: boolean;
  anchorOrigin: ToastAnchorOrigin;
};

const TOAST_VARIANTS: Record<ToastVariant, ToastVariantConfig> = {
  error: {
    status: "error",
    showCloseButton: true,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  },
  "no-icon": {
    status: null,
    showCloseButton: false,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  },
  complete: {
    status: "complete",
    showCloseButton: false,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  },
  undo: {
    status: "complete",
    showCloseButton: true,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  },
  close: {
    status: null,
    showCloseButton: true,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  },
  chrome: {
    status: "error",
    showCloseButton: true,
    anchorOrigin: { vertical: "top", horizontal: "center" },
  },
};

const resolveToastVariant = (
  variant: ToastVariant | undefined,
  type: ToastType,
): ToastVariant => {
  if (variant) return variant;
  if (type === "notice") return "no-icon";
  return type;
};

const ToastStatusGlyph: React.FC<{ status: ToastStatus }> = ({ status }) => {
  const color =
    status === "error"
      ? designTokens.fill.icon.yellow50.dark
      : designTokens.fill.text.primary50.dark;

  return (
    <Box
      component="span"
      data-testid="toast-status-icon"
      data-toast-status={status}
      aria-hidden="true"
      sx={{
        color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        flexShrink: 0,
      }}
    >
      <Icon
        name={status === "error" ? "error-solid-24" : "complete-solid-24"}
        aria-hidden="true"
      />
    </Box>
  );
};

const ToastCloseGlyph = () => (
  <Icon name="close-16" size={14} aria-hidden="true" />
);

export interface ToastProps {
  /** Figma visual form. When omitted, the legacy `type` maps to its matching form. */
  variant?: ToastVariant;
  type?: ToastType;
  message?: string;
  open?: boolean;
  onClose?: (event?: React.SyntheticEvent | Event, reason?: string) => void;
  duration?: number | null;
  showIcon?: boolean;
  showCloseButton?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  /** action을 유지한 채 비활성으로 표시한다. 클릭은 무시된다. */
  actionDisabled?: boolean;
  anchorOrigin?: ToastAnchorOrigin;
  sx?: SxProps<Theme>;
}

/**
 * Toast Component
 * - 피그마 디자인에 맞춘 토스트 알림 컴포넌트
 * - Error, No icon, Complete, Undo, Close, Chrome 형태 지원
 * - 기본 bottom-center, Chrome top-center 위치
 * - 기본 3초이며 caller duration/action 계약을 우선
 */
export const ToastSurface: React.FC<ToastProps> = ({
  type = "error",
  variant,
  message = "",
  open = false,
  onClose,
  duration = 3000,
  showIcon,
  showCloseButton,
  actionLabel,
  onAction,
  actionDisabled = false,
  anchorOrigin,
  sx = {},
}) => {
  const effectiveVariant = resolveToastVariant(variant, type);
  const variantConfig = TOAST_VARIANTS[effectiveVariant];
  const effectiveAnchorOrigin = anchorOrigin ?? variantConfig.anchorOrigin;
  const shouldShowIcon = showIcon ?? variantConfig.status !== null;
  const shouldShowCloseButton =
    showCloseButton ?? variantConfig.showCloseButton;
  const isTop = effectiveAnchorOrigin.vertical === "top";
  const isCenter = effectiveAnchorOrigin.horizontal === "center";
  const isLeft = effectiveAnchorOrigin.horizontal === "left";
  const toastTokens = designTokens.semantic.toast.default;
  const actionColor =
    effectiveVariant === "error"
      ? designTokens.fill.text.error.dark
      : toastTokens.action.color;
  const stackId = React.useId();
  const onCloseRef = React.useRef(onClose);
  const [, refreshStack] = React.useReducer((revision) => revision + 1, 0);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    toastStackListeners.add(refreshStack);
    return () => {
      toastStackListeners.delete(refreshStack);
    };
  }, []);

  React.useEffect(() => {
    if (!open) {
      unregisterToast(stackId);
      return undefined;
    }

    registerToast({
      id: stackId,
      close: () => onCloseRef.current?.(),
    });
    return () => unregisterToast(stackId);
  }, [open, stackId]);

  // ✕ 버튼이 있는 Toast는 자동 종료 대상이 아니다.
  const effectiveDuration = shouldShowCloseButton ? null : duration;
  const [enterCompleted, setEnterCompleted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setEnterCompleted(false);
      return undefined;
    }
    // transition callback이 오지 않는 환경에서도 같은 시점에 타이머를 무장한다.
    const enterTimeoutId = window.setTimeout(
      () => setEnterCompleted(true),
      TOAST_ENTER_ANIMATION_MS,
    );
    return () => window.clearTimeout(enterTimeoutId);
  }, [open]);

  const handleEntered = React.useCallback(() => setEnterCompleted(true), []);

  // 본문·바깥 클릭은 Toast를 닫지 않는다. 닫기는 ✕ 또는 자동 종료만 수행한다.
  const handleSnackbarClose = React.useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") return;
      onCloseRef.current?.(event, reason);
    },
    [],
  );

  // 자동 종료 타이머는 등장 애니메이션 완료 시점부터 시작하고,
  // 탭이 비활성이면 일시정지했다가 다시 활성화되면 남은 시간을 이어서 센다.
  React.useEffect(() => {
    if (!open || !enterCompleted || effectiveDuration == null) return undefined;

    let remaining = effectiveDuration;
    let startedAt = Date.now();
    let timeoutId: number | null = null;

    const clear = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    const start = () => {
      clear();
      startedAt = Date.now();
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        onCloseRef.current?.(undefined, "timeout");
      }, remaining);
    };
    const pause = () => {
      if (timeoutId === null) return;
      remaining = Math.max(0, remaining - (Date.now() - startedAt));
      clear();
    };
    const handleVisibilityChange = () => {
      if (document.hidden) pause();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clear();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // message가 바뀌면 같은 Toast의 중복 호출로 보고 타이머를 리셋한다.
  }, [open, enterCompleted, effectiveDuration, message]);

  const stackIndex = open ? getToastStackIndex(stackId) : null;
  const stackOffset =
    (stackIndex ?? 0) * designTokens.semantic.toast.default.stack.step;

  return (
    <Snackbar
      open={open && stackIndex !== null}
      autoHideDuration={null}
      onClose={handleSnackbarClose}
      TransitionProps={{ onEntered: handleEntered }}
      anchorOrigin={effectiveAnchorOrigin}
      data-toast-variant={effectiveVariant}
      data-toast-stack-index={stackIndex ?? undefined}
      sx={{
        // Snackbar container는 surface보다 넓게 잡힐 수 있다. 그 여백이 아래
        // 레이어의 클릭을 가로채지 않도록 container는 이벤트를 통과시키고
        // Toast surface에서만 다시 받는다.
        pointerEvents: "none",
        ...(isTop
          ? {
              top: `${toastTokens.position.verticalOffset} !important`,
              bottom: "auto !important",
            }
          : {
              bottom: `${toastTokens.position.verticalOffset} !important`,
              top: "auto !important",
            }),
        ...(isCenter
          ? {
              left: `${toastTokens.position.centerLeft} !important`,
              transform: `${toastTokens.position.centerTransform} !important`,
              right: "auto !important",
            }
          : isLeft
            ? {
                left: `${toastTokens.position.horizontalOffset} !important`,
                right: "auto !important",
                transform: "none !important",
              }
            : {
                right: `${toastTokens.position.horizontalOffset} !important`,
                left: "auto !important",
                transform: "none !important",
              }),
        ...sx,
        ...(stackOffset > 0
          ? isTop
            ? { marginTop: `${stackOffset}px !important` }
            : { marginBottom: `${stackOffset}px !important` }
          : {}),
      }}
    >
      <Box
        data-toast-surface
        sx={{
          pointerEvents: "auto",
          backgroundColor: toastTokens.surface.background,
          borderRadius: toastTokens.surface.radius,
          px: toastTokens.surface.paddingX,
          py: toastTokens.surface.paddingY,
          minHeight: toastTokens.surface.minHeight,
          display: "flex",
          gap: toastTokens.surface.gap,
          alignItems: "center",
          // no-icon(notice) toast는 Figma처럼 내용 폭(min 320px)이고, 그 외는 477px 고정 폭이다.
          width: effectiveVariant === "no-icon" ? "auto" : toastTokens.surface.width,
          minWidth: effectiveVariant === "no-icon" ? toastTokens.surface.noIconMinWidth : undefined,
          maxWidth: toastTokens.surface.maxWidth,
          boxSizing: "border-box",
          boxShadow: toastTokens.surface.shadow,
        }}
      >
        {shouldShowIcon && variantConfig.status && (
          <ToastStatusGlyph status={variantConfig.status} />
        )}

        <Typography
          sx={{
            ...designTokens.typography.styles.body,
            color: toastTokens.text.color,
            flex: 1,
            lineHeight: toastTokens.text.lineHeight,
          }}
        >
          {message}
        </Typography>

        {actionLabel && onAction && (
          <ButtonBase
            onClick={onAction}
            disabled={actionDisabled}
            disableRipple
            data-toast-action-tone={
              effectiveVariant === "error" ? "error" : "primary"
            }
            data-toast-action-disabled={actionDisabled ? "true" : undefined}
            sx={{
              ...designTokens.typography.styles.body,
              color: actionDisabled
                ? toastTokens.action.disabledColor
                : actionColor,
              px: toastTokens.action.paddingX,
              py: toastTokens.action.paddingY,
              borderRadius: toastTokens.action.radius,
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: actionDisabled
                  ? "transparent"
                  : toastTokens.action.hoverBackground,
              },
              "&:focus-visible": {
                outline: `${toastTokens.action.focusOutlineWidth} solid ${actionColor}`,
                outlineOffset: toastTokens.action.focusOutlineOffset,
              },
            }}
          >
            {actionLabel}
          </ButtonBase>
        )}

        {shouldShowCloseButton && (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close toast"
            sx={{
              padding: toastTokens.closeButton.padding,
              minWidth: toastTokens.closeButton.minSize,
              minHeight: toastTokens.closeButton.minSize,
              color: toastTokens.closeButton.color,
              "&:hover": {
                backgroundColor: toastTokens.closeButton.hoverBackground,
              },
            }}
          >
            <ToastCloseGlyph />
          </IconButton>
        )}
      </Box>
    </Snackbar>
  );
};

const Toast: React.FC<ToastProps> = (props) => {
  const host = React.useContext(PersistentToastContext);
  const id = React.useId();
  const current = React.useRef(props);
  const mounted = React.useRef(false);
  const dismissed = React.useRef(false);
  current.current = props;
  const variant = resolveToastVariant(props.variant, props.type ?? "error");
  // shortcut: owner-bound Retry stays local until its post-navigation recovery flow is defined.
  const persistent = variant === "chrome" || (variant === "error" &&
    (!props.onAction || props.actionLabel === REPORT_ISSUE_TOAST_LABEL));
  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  React.useLayoutEffect(() => {
    if (!props.open) {
      dismissed.current = false;
      return;
    }
    if (!host || !persistent || dismissed.current) return;
    const close = () => {
      host.dismiss(id);
      // An older retained error must not close a newer message owned by the same component.
      if (mounted.current && current.current.open && current.current.message === props.message) {
        dismissed.current = true;
        current.current.onClose?.();
      }
    };
    host.show(id, {
      ...props,
      onClose: close,
      onAction: props.onAction ? () => {
        close();
        if (props.actionLabel === REPORT_ISSUE_TOAST_LABEL) openReportIssue("toast");
        else props.onAction?.();
      } : undefined,
    });
  });
  return host && persistent ? null : <ToastSurface {...props} />;
};

export default Toast;
